// src/services/api.js

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// ========================================
// Helpers
// ========================================

function withTimeout(ms = 15000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  return { controller, clear: () => clearTimeout(id) };
}

function getCustomerToken() {
  return localStorage.getItem("token");
}

function setCustomerToken(token) {
  if (token) localStorage.setItem("token", token);
}

function clearCustomerSession() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

function getAdminToken() {
  return localStorage.getItem("adminToken");
}

function setAdminToken(token) {
  if (token) localStorage.setItem("adminToken", token);
}

function clearAdminSession() {
  localStorage.removeItem("adminToken");
  localStorage.removeItem("adminUser");
  localStorage.removeItem("adminProfile");
}

async function fetchJson(url, options = {}, { isAdmin = false } = {}) {
  const { controller, clear } = withTimeout(15000);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });

    let data = null;
    try {
      data = await response.json();
    } catch (e) {
      // ignore JSON parse errors
    }

    if (!response.ok) {
      // auto-clear session on 401 (expired/invalid token)
      if (response.status === 401) {
        if (isAdmin) clearAdminSession();
        else clearCustomerSession();
      }

      const message =
        (data && (data.message || data.error)) ||
        `Request failed with status ${response.status}`;
      throw new Error(message);
    }

    return data;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new Error(
        "Request timed out. Is the backend running on " + API_BASE_URL + "?"
      );
    }
    throw err;
  } finally {
    clear();
  }
}

// ========================================
// COCKPIT (ADMIN) AUTH API
// ========================================

export async function cockpitLogin(payload) {
  const url = `${API_BASE_URL}/api/v2/cockpit/login`;

  const data = await fetchJson(
    url,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    { isAdmin: true }
  );

  // backend returns { ok:true, token, admin, user }
  if (data?.token) {
    setAdminToken(data.token);
    localStorage.setItem("adminUser", JSON.stringify(data.user));
    localStorage.setItem("adminProfile", JSON.stringify(data.admin));
  }

  return data;
}

export async function cockpitMe() {
  const url = `${API_BASE_URL}/api/admin/auth/me`;
  const token = getAdminToken();

  return fetchJson(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    { isAdmin: true }
  );
}

export function cockpitLogout() {
  clearAdminSession();
}

// ========================================
// CUSTOMER AUTH API
// ========================================

export async function registerUser(payload) {
  const url = `${API_BASE_URL}/api/v2/auth/register`;
  console.log("➡️ Calling register endpoint:", url, payload);

  return fetchJson(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function loginUser(payload) {
  const url = `${API_BASE_URL}/api/v2/auth/login`;
  console.log("➡️ Calling login endpoint:", url);

  const data = await fetchJson(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  // ✅ IMPORTANT: persist customer token so the app can proceed
  // backend may return { token } or { accessToken } depending on implementation
  const token = data?.token || data?.accessToken;
  if (token) setCustomerToken(token);

  // store user if returned
  if (data?.user) localStorage.setItem("user", JSON.stringify(data.user));

  return data;
}

export function customerLogout() {
  clearCustomerSession();
}

export async function getProfile() {
  const url = `${API_BASE_URL}/api/v2/users/profile`;
  const token = getCustomerToken();

  return fetchJson(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
    { isAdmin: false }
  );
}

export async function updateProfile(payload) {
  const url = `${API_BASE_URL}/api/v2/users/profile`;
  const token = getCustomerToken();

  return fetchJson(
    url,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    },
    { isAdmin: false }
  );
}

// ========================================
// RECIPIENTS API
// ========================================

export async function getRecipients(filters = {}) {
  const token = getCustomerToken();

  const queryParams = new URLSearchParams();
  if (filters.search) queryParams.append("search", filters.search);
  if (filters.recipientType)
    queryParams.append("recipientType", filters.recipientType);
  if (filters.recipientCountry)
    queryParams.append("recipientCountry", filters.recipientCountry);
  if (filters.isFavorite) queryParams.append("isFavorite", "true");

  const queryString = queryParams.toString();
  const url = `${API_BASE_URL}/api/v2/recipients${
    queryString ? `?${queryString}` : ""
  }`;

  return fetchJson(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

export async function getRecipientById(recipientId) {
  const token = getCustomerToken();

  return fetchJson(`${API_BASE_URL}/api/v2/recipients/${recipientId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

export async function createRecipient(recipientData) {
  const token = getCustomerToken();

  return fetchJson(`${API_BASE_URL}/api/v2/recipients`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(recipientData),
  });
}

export async function updateRecipient(recipientId, recipientData) {
  const token = getCustomerToken();

  return fetchJson(`${API_BASE_URL}/api/v2/recipients/${recipientId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(recipientData),
  });
}

export async function deleteRecipient(recipientId) {
  const token = getCustomerToken();

  return fetchJson(`${API_BASE_URL}/api/v2/recipients/${recipientId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

export async function toggleRecipientFavorite(recipientId) {
  const token = getCustomerToken();

  return fetchJson(`${API_BASE_URL}/api/v2/recipients/${recipientId}/favorite`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

export async function verifyBankAccount(recipientId) {
  const token = getCustomerToken();

  return fetchJson(`${API_BASE_URL}/api/v2/recipients/${recipientId}/verify`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
}

// ========================================
// MOBILE TOP-UP API
// ========================================

/**
 * Create a mobile top-up (airtime) order.
 * Requires an Idempotency-Key so a retry never tops up twice.
 */
export async function createAirtimeOrder(payload, idempotencyKey) {
  const token = getCustomerToken();
  return fetchJson(`${API_BASE_URL}/api/v2/airtime/orders`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(payload),
  });
}

/** Fetch one top-up order by its ZoomPay reference. */
export async function getAirtimeOrder(reference) {
  const token = getCustomerToken();
  return fetchJson(`${API_BASE_URL}/api/v2/airtime/orders/${reference}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ========================================
// PARENT ORDER API
// ========================================

/**
 * Build a priced quote for a multi-recipient, multi-product order.
 * The quote is persisted server-side and is the ONLY source of amounts —
 * the browser never asserts prices.
 */
export async function createOrderQuote(payload) {
  const token = getCustomerToken();
  return fetchJson(`${API_BASE_URL}/api/v2/orders/quote`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

/**
 * Create the parent order from an ACCEPTED quote.
 * Requires an Idempotency-Key so a retry never creates two orders.
 */
export async function createParentOrder(payload, idempotencyKey) {
  const token = getCustomerToken();
  return fetchJson(`${API_BASE_URL}/api/v2/orders`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(payload),
  });
}

/** Fetch one parent order by reference (owner-scoped). */
export async function getParentOrder(reference) {
  const token = getCustomerToken();
  return fetchJson(`${API_BASE_URL}/api/v2/orders/${reference}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
}

/** Customer wallet balances: { balances: { CAD, USD, KES }, wallets: [...] } */
export async function getWalletBalance() {
  const token = getCustomerToken();
  return fetchJson(`${API_BASE_URL}/api/v2/wallet/balance`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });
}

